import {
    getApiCoa,
    getApiCoaAccountsByCoaId,
    postApiCoaAccountsPermissions,
    deleteApiCoaAccountsPermissions,
    postApiCoaAccountsMappingsByCoaId,
    getApiCoaAuditLogsByCoaId,
    postApiCoaDraftCoa,
    getApiCoaCoaDefinitionByCoaId,
    postApiCoaDraftCoaAccountsByCoaId,
    postApiCoaCompleteCoaByCoaId,
    deleteApiCoaDraftCoaByCoaId,
    postApiCoaFiles,
    putApiCoaDraftCoaByCoaId,
    getApiCoaMappingTableByCoaId,
} from "@/generated";

import { handleApiResponseAssertData } from "./handleApiResponseAssertData";
import { handleApiResponse } from "./handleApiResponse";
import type {
    TGrantAccountPermissionsRequest,
    TDraftChartOfAccountsDetail,
    TNewCoADefinition,
    TRevokeAccountPermissionsRequest,
} from "../apiODIN";
import { ApiODINMessageTypeCoAExtraction } from "../TApiODINMessageType";
import type {
    TApiODINMessageCoAExtraction,
    TApiResponseCoAExtractionSuccess,
} from "./TApiResponseCoAExtraction";

export const createApiAccounts = ({
    getAccessToken,
    baseWsUrl,
}: {
    getAccessToken: () => Promise<string>;
    baseWsUrl: string;
}) => ({
    getApiAccountsByCoaId: (coaId: number) =>
        handleApiResponseAssertData(
            () => getApiCoaAccountsByCoaId({ path: { coa_id: coaId } }),
            "Failed to get accounts by COA ID",
        ),
    getAllAvailableCoAsForUser: () =>
        handleApiResponseAssertData(
            () => getApiCoa(),
            "Failed to get available COAs",
        ),
    addAccountPermissions: (
        accountPermissions: TGrantAccountPermissionsRequest,
    ) =>
        handleApiResponse(
            () => postApiCoaAccountsPermissions({ body: accountPermissions }),
            "Failed to add accounts permissions",
        ),
    deleteAccountPermissions: (
        accountPermissions: TRevokeAccountPermissionsRequest,
    ) =>
        handleApiResponse(
            () => deleteApiCoaAccountsPermissions({ body: accountPermissions }),
            "Failed to delete accounts permissions",
        ),
    saveAlteredCustomAccounts: (params: {
        coaId: number;
        alteredCustomAccounts: {
            accountCode: string;
            mappedAccountCode: {
                from: string | null;
                to: string | null;
            };
        }[];
    }) =>
        handleApiResponse(
            () =>
                postApiCoaAccountsMappingsByCoaId({
                    body: {
                        alteredCustomAccounts: params.alteredCustomAccounts,
                    },
                    path: { coa_id: params.coaId },
                }),
            "Failed to save altered custom accounts",
        ),
    getAuditLogsByCoaId: (coaId: number) =>
        handleApiResponseAssertData(
            () => getApiCoaAuditLogsByCoaId({ path: { coa_id: coaId } }),
            "Failed to get audit logs by COA ID",
        ),
    createDraftCoa: (draftCoa: TNewCoADefinition) =>
        handleApiResponseAssertData(
            () => postApiCoaDraftCoa({ body: draftCoa }),
            "Failed to create draft COA",
        ),
    getCoaDefinitionByCoaId: (coaId: number) =>
        handleApiResponseAssertData(
            () => getApiCoaCoaDefinitionByCoaId({ path: { coa_id: coaId } }),
            "Failed to get COA definition by COA ID",
        ),
    insertCoaAccounts: (
        coaId: number,
        accounts: TDraftChartOfAccountsDetail[],
    ) =>
        handleApiResponse(
            () =>
                postApiCoaDraftCoaAccountsByCoaId({
                    body: accounts,
                    path: { coa_id: coaId },
                }),
            "Failed to insert COA accounts",
        ),
    completeCoA: (coaId: number) =>
        handleApiResponseAssertData(
            () => postApiCoaCompleteCoaByCoaId({ path: { coa_id: coaId } }),
            "Failed to complete COA",
        ),
    deleteDraftCoA: (coaId: number) =>
        handleApiResponse(
            () => deleteApiCoaDraftCoaByCoaId({ path: { coa_id: coaId } }),
            "Failed to delete draft COA",
        ),
    getApiCoaMappingTableByCoaId: (coaId: number) =>
        handleApiResponseAssertData(
            () => getApiCoaMappingTableByCoaId({ path: { coa_id: coaId } }),
            "Failed to get mapping table",
        ),
    updateDraftCoA: (coaId: number, draftCoa: TNewCoADefinition) =>
        handleApiResponse(
            () =>
                putApiCoaDraftCoaByCoaId({
                    path: { coa_id: coaId },
                    body: draftCoa,
                }),
            "Failed to update draft COA",
        ),
    addCoAFile: (coaId: number | null, file: Blob | File) =>
        handleApiResponseAssertData(
            () =>
                postApiCoaFiles({
                    body: { upload_file: file },
                    query: { coa_id: coaId ?? undefined },
                }),
            "Failed to add COA file",
        ),
    extractAccounts: async ({
        coaId,
        onConnectionOpened,
        onConnectionClosed,
        onError,
        onProgress,
        onExtractionSucceeded,
        fileId,
    }: {
        coaId: number | null;
        onConnectionOpened?: () => void;
        onConnectionClosed?: () => void;
        onError?: ({ errorMessage }: { errorMessage: string }) => void;
        onProgress?: (params: { progress: number; message: string }) => void;
        onExtractionSucceeded?: (
            message: Omit<TApiResponseCoAExtractionSuccess, "type">,
        ) => void;
        fileId: number;
    }): Promise<TApiResponseCoAExtractionSuccess> => {
        const ERROR_MESSAGES = {
            UPLOAD_FAILURE: "Error: The file could not be uploaded.",
            PROCESSING_FILE_FAILURE: "There was an error processing the file.",
        };

        const accessToken = await getAccessToken();

        return new Promise<TApiResponseCoAExtractionSuccess>(
            (resolve, reject) => {
                const webSocket = new WebSocket(
                    `${baseWsUrl}/ws/extractAccounts`,
                );
                webSocket.binaryType = "blob";
                let isSettled = false;

                const rejectAndClose = (errorMessage: string) => {
                    if (isSettled) return;
                    isSettled = true;
                    onError?.({ errorMessage });
                    reject(new Error(errorMessage));
                    webSocket.close();
                };

                webSocket.addEventListener("open", () => {
                    webSocket.send(
                        JSON.stringify({
                            coaId,
                            fileId,
                            token: accessToken,
                        }),
                    );
                    onConnectionOpened?.();
                });

                webSocket.addEventListener("close", event => {
                    onConnectionClosed?.();
                    if (isSettled) return;
                    isSettled = true;
                    reject(
                        new Error(
                            event.reason ||
                                "The connection was interrupted before the account extraction finished.",
                        ),
                    );
                });

                webSocket.addEventListener("error", () => {
                    rejectAndClose(ERROR_MESSAGES.UPLOAD_FAILURE);
                });

                webSocket.addEventListener(
                    "message",
                    (event: MessageEvent<string>) => {
                        let message: TApiODINMessageCoAExtraction;
                        try {
                            message = JSON.parse(
                                event.data,
                            ) as TApiODINMessageCoAExtraction;
                        } catch {
                            rejectAndClose(
                                ERROR_MESSAGES.PROCESSING_FILE_FAILURE,
                            );
                            return;
                        }

                        if (
                            message.type ===
                            ApiODINMessageTypeCoAExtraction.PROGRESS
                        ) {
                            onProgress?.({
                                progress: message.contents.percentage,
                                message: message.contents.status,
                            });
                            return;
                        }

                        if (
                            message.type ===
                            ApiODINMessageTypeCoAExtraction.FAILURE
                        ) {
                            rejectAndClose(message.contents);
                            return;
                        }

                        if (
                            message.type ===
                            ApiODINMessageTypeCoAExtraction.COA_EXTRACTION_SUCCESS
                        ) {
                            if (isSettled) return;
                            isSettled = true;
                            onExtractionSucceeded?.(message);
                            resolve(message);
                            return;
                        }

                        console.error(
                            `Unknown message type: ${JSON.stringify(message)}`,
                        );
                        rejectAndClose(ERROR_MESSAGES.PROCESSING_FILE_FAILURE);
                    },
                );
            },
        );
    },
});
